import {
  HighContrastSelector,
  IRawStyle,
  memoizeFunction,
  mergeStyleSets,
} from "@fluentui/react";
import { ITheme } from "@fluentui/react/lib/Styling";
import {
  IColorPickerGridCellStyleProps,
  IColorPickerGridCellStyles,
} from "@fluentui/react/lib/SwatchColorPicker";
import { IStyleFunction } from "@fluentui/react/lib/Utilities";

import {
  baseFabricFocusAfterSelector,
  baseFabricFocusSelector,
  baseFabricFocusVisibleSelector,
  baseFabricHoverSelector,
  getInnerBorderStyles,
  getStandardControlSemanticColorsWithTheme,
} from "@1js/acui-common";

function getSvgSelectorInteractiveStyles(borderColor: string): IRawStyle {
  return {
    width: 16,
    height: 16,
    border: `4px solid ${borderColor}`,
    boxShadow: "none",
    padding: 2,
    margin: 1,
    color: "transparent",
    selectors: {
      [HighContrastSelector]: { borderColor: "Highlight" },
    },
  };
}

function getSvgPassiveStyles(): IRawStyle {
  return {
    border: "none",
    flex: "0 0 auto",
    width: 20,
    height: 20,
    padding: 5,
    boxSizing: "content-box",
    stroke: "currentColor",
    strokeWidth: 2,
    selectors: {
      [HighContrastSelector]: { strokeWidth: 1 },
    },
  };
}

function getSelectedSvgStyles(theme: ITheme): IRawStyle {
  const standardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  const selectedColor = standardControlSemanticColors.toggledBackgroundColor;
  const boxShadowColor =
    standardControlSemanticColors.colorPickerSelectedBorderColor;
  return {
    border: "3px solid " + selectedColor,
    boxShadow: `0 0 0 1px ${boxShadowColor}`,
    width: 16,
    height: 16,
    padding: 2,
    margin: 1,
  };
}

export const getColorGridCellStyles = memoizeFunction(
  (
    customStyles?: IStyleFunction<
      IColorPickerGridCellStyleProps,
      IColorPickerGridCellStyles
    >
  ) => {
    return memoizeFunction(
      (props: IColorPickerGridCellStyleProps): IColorPickerGridCellStyles => {
        // getStyles will return the base styles that should be applied to every color picker cell
        // customStyles will a partial IColorPickerGridCellStyles with anything that component wants to
        // override about the cell styles.
        return mergeStyleSets(
          getStyles(props),
          customStyles && customStyles(props)
        );
      }
    );
  }
);

const svgClass = "gridCell-SVG";

const getStyles = memoizeFunction(
  (props: IColorPickerGridCellStyleProps): IColorPickerGridCellStyles => {
    const { theme, disabled, selected, circle, isWhite } = props;
    const standardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    const hoverAndFocusBackgroundColor =
      standardControlSemanticColors.hoverBackgroundColor;
    return {
      colorCell: [
        {
          backgroundColor: "transparent",
          padding: 0,
          width: 30,
          height: 30,
          overflow: "visible",
          position: "relative",
          boxSizing: "border-box",
          display: "inline-block",
          border: "none !important", // this is needed due to a high contrast firefox issue where the border does not actually get set to none, it gets set to 3px, without the !important
          background: "transparent",
          cursor: "pointer",
          textAlign: "center",
          verticalAlign: "top",
          userSelect: "none",
          color: "transparent",
          selectors: {
            ":disabled": { pointerEvents: "auto" },
            [`${baseFabricFocusSelector}, ${baseFabricFocusVisibleSelector} &:focus::after`]:
              {
                outline: "none",
              },
            ":focus": { padding: 0 },
            ":hover": {
              border: "none",
              outline: "none",
              padding: 0,
              selectors: {
                [`.${svgClass}`]: {
                  height: 20,
                  width: 20,
                  border: "none",
                  color: "transparent",
                  padding: 5,
                },
              },
            },
            // hover only
            [`:focus:hover .${svgClass}`]: getSvgSelectorInteractiveStyles(
              hoverAndFocusBackgroundColor
            ),
            // keyboard focus
            [`${baseFabricFocusSelector} .${svgClass}`]:
              getSvgSelectorInteractiveStyles(hoverAndFocusBackgroundColor),
            // hover & focus
            [`${baseFabricHoverSelector} .${svgClass}`]:
              getSvgSelectorInteractiveStyles(hoverAndFocusBackgroundColor),
            // active (including active & hover)
            [`:active .${svgClass}, :active:hover .${svgClass}`]:
              getSvgSelectorInteractiveStyles(
                standardControlSemanticColors.pressedBackgroundColor
              ),
            [baseFabricFocusAfterSelector]: getInnerBorderStyles({
              width: 2,
              color: standardControlSemanticColors.focusedBorderColor,
            }),
          },
        },
        isWhite && {
          selectors: {
            [`.${svgClass}`]: {
              padding: 0,
              border:
                "1px solid " +
                standardControlSemanticColors.colorPickerWhiteCellBorderColor,
              margin: 5,
              height: 20,
              width: 20,
            },
          },
        },
        circle && {
          selectors: {
            [`.${svgClass}`]: { borderRadius: "100%" },
          },
        },
        selected && {
          selectors: {
            [`.${svgClass}`]: [
              {
                selectors: {
                  [HighContrastSelector]: { borderColor: "Highlight" },
                },
              },
              getSelectedSvgStyles(theme),
            ],
            [`:active .${svgClass}`]: {
              borderWidth: 3,
            },
            [`:focus:hover .${svgClass}`]: {
              borderWidth: 3,
            },
            ["&:hover::before"]: {
              position: "relative",
            },
          },
        },
        disabled && {
          color: standardControlSemanticColors.colorPickerDisabledCellColor,
          cursor: "default",
          opacity: 1,
          fillOpacity: 0.3,
          selectors: {
            [HighContrastSelector]: {
              opacity: 1,
              selectors: {
                [`:focus .${svgClass}, :hover:focus .${svgClass}`]: {
                  borderColor: "Highlight",
                },
              },
            },
            [`.${svgClass}`]: {
              color: "transparent",
            },
            [`:active .${svgClass}`]: getSvgPassiveStyles(),
          },
        },
      ],
      svg: [getSvgPassiveStyles(), svgClass],
    };
  }
);
